import React from 'react';
import PropTypes from 'prop-types';
import { formatNumberWithSeparators } from '@tradera/blueprint/utils';
import MultiCurrency from 'tradera-components/multi-currency/multi-currency';
import { useTranslator } from 'tradera-lang/translate';
import { formatDate } from 'tradera-utils/date';
import TimeLeft from '../helpers/time-left';
import { ITEM_STATES } from 'tradera-state/views/view-item/constants';
import { AnimateOnValueChange } from './animate-on-value-change';
import './bid-details.scss';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';

export const BidDetails = ({
    bidCount,
    endDate,
    fixedPrice,
    hasEnded,
    isCountdownEnabled,
    isEnding,
    itemState,
    leadingBidAmount,
    onShowBids,
    openingBid,
    preferredCurrency,
    secondsLeft,
    serverSecondsLeft
}) => {
    const { t } = useTranslator();
    const isVipBidAnimationEnabled = useIsFeatureEnabled('vip-bid-animation');

    const hasBids = bidCount > 0;
    const amountDetails = getAmountDetails(
        t,
        itemState,
        fixedPrice,
        leadingBidAmount,
        openingBid,
        hasBids
    );

    const amountTitle = (
        <p className="text-nowrap mb-0 bid-details-amount-title">
            {amountDetails.title}
        </p>
    );
    const amountValue = (
        <>
            <p
                className={`text-nowrap heading-madrid mb-0 bid-details-amount ${
                    itemState === ITEM_STATES.ENDED_AUCTION_WITH_WINNING_BID
                        ? 'text-success'
                        : ''
                }`}>
                {isVipBidAnimationEnabled ? (
                    <AnimateOnValueChange trigger={bidCount}>
                        {formatNumberWithSeparators(amountDetails.amount)}{' '}
                        {t('vip_currency')}
                    </AnimateOnValueChange>
                ) : (
                    <>
                        {formatNumberWithSeparators(amountDetails.amount)}{' '}
                        {t('vip_currency')}
                    </>
                )}
            </p>
            <MultiCurrency
                amountInSek={amountDetails.amount}
                className="font-weight-normal multicurrency mb-0 text-md-right"
                preferredCurrency={preferredCurrency}
                tagName="p"
            />
        </>
    );
    const timeTitle =
        isEnding || hasEnded ? (
            <p className="text-nowrap mb-0 bid-details-time-title">
                {t('vip_hasEndedTime')} {formatDate(new Date(endDate))}
            </p>
        ) : (
            <p className="text-nowrap mb-0 bid-details-time-title">
                {t('vip_willEndTime')} {formatDate(new Date(endDate))}
            </p>
        );
    const timeValue =
        isEnding || hasEnded ? (
            <p
                className={`heading-london mb-0 ${
                    isEnding ? 'text-danger' : ''
                }`}>
                {hasEnded ? t('vip_ended') : t('vip_ending')}
            </p>
        ) : (
            <p className="heading-london mb-0">
                <TimeLeft
                    seconds={secondsLeft}
                    serverSecondsLeft={serverSecondsLeft}
                    isCountdownEnabled={isCountdownEnabled}
                />
            </p>
        );
    const bidTitle = (
        <p className="mb-0 bid-details-bids-title">
            {t('vip_bid')}{' '}
            {hasBids && (
                <span>
                    (
                    <a
                        className="text-primary text-lowercase"
                        href="#"
                        onClick={e => {
                            e.preventDefault();
                            onShowBids();
                        }}>
                        <u>{t('vip_show')}</u>
                    </a>
                    )
                </span>
            )}
        </p>
    );
    const bidValue = (
        <p className={`text-nowrap heading-london mb-0`}>
            {bidCount} {t('vip_amount')}
        </p>
    );

    return (
        <section className="bid-details d-flex flex-md-column justify-content-between py-1 pt-md-0 pb-md-2">
            <div className="d-flex flex-column flex-md-row align-items-baseline justify-content-between mb-md-2">
                <div className="mb-1 mb-md-0">{amountTitle}</div>
                <div>{amountValue}</div>
            </div>
            <div className="d-flex flex-column flex-md-row justify-content-between mb-md-2 text-center">
                <div className="mb-1 mb-md-0">{timeTitle}</div>
                <div className="my-auto">{timeValue}</div>
            </div>
            <div className="d-flex flex-column flex-md-row justify-content-between text-right">
                <div className="mb-1 mb-md-0">{bidTitle}</div>
                <div className="my-auto">{bidValue}</div>
            </div>
        </section>
    );
};

BidDetails.propTypes = {
    bidCount: PropTypes.number,
    endDate: PropTypes.string.isRequired,
    hasEnded: PropTypes.bool.isRequired,
    isCountdownEnabled: PropTypes.bool.isRequired,
    itemState: PropTypes.string.isRequired,
    leadingBidAmount: PropTypes.number,
    onShowBids: PropTypes.func,
    openingBid: PropTypes.number,
    secondsLeft: PropTypes.number,
    serverSecondsLeft: PropTypes.number
};

BidDetails.defaultProps = {
    className: '',
    hasLeadingBid: false,
    hasLostLeadingBid: false
};

export const getAmountDetails = (
    t,
    itemState,
    fixedPrice,
    leadingBid,
    openingBid,
    hasBids
) => {
    if (itemState === ITEM_STATES.ENDED_AUCTION_BIN_BOUGHT_AS_BIN) {
        return {
            title: t('vip_finalPrice'),
            amount: fixedPrice
        };
    }

    if (itemState === ITEM_STATES.ENDED_AUCTION_WITH_WINNING_BID) {
        return {
            title: t('vip_finalPrice'),
            amount: leadingBid
        };
    }

    if (hasBids) {
        return {
            title: t('vip_leadingBid'),
            amount: leadingBid
        };
    }
    return {
        title: t('vip_openingBid'),
        amount: openingBid
    };
};
